import { TaskColumn, type TaskSortValue } from "@common/types/views/tasksView";
import type { View } from "@common/types/views/views";
import { separateColumnsByPin } from "@design-system/ViewToolbar/columnSettingsUtils";
import type { ViewConfig } from "@lib/views";
import type { NestedTaskRow } from "../types";

type ViewColumns = View["columns"];

/** List's column definitions, as its column settings read them. */
export type TaskColumnConfigs = ViewConfig<
    NestedTaskRow,
    TaskColumn,
    TaskSortValue
>["columns"];

/**
 * The List columns a card has a field for. The title, the row actions, the
 * status and the assignee (always on the card) and the client (its own
 * field) never are.
 * `TaskBoardCard` keys its renderers by these, so an id without one, or a
 * renderer without an id, fails typecheck.
 */
const CARD_FIELD_COLUMNS = [
    TaskColumn.DUE_DATE,
    TaskColumn.ENTITIES,
    TaskColumn.PRIORITY,
    TaskColumn.ESTIMATE,
    TaskColumn.GROUP,
    TaskColumn.CLOSE_STAGE,
    TaskColumn.CLOSE,
    TaskColumn.SERVICE_PERIOD,
    TaskColumn.VISIBILITY,
    TaskColumn.CREATED_AT,
    TaskColumn.CREATED_BY,
    TaskColumn.UPDATED_AT,
    TaskColumn.COMPLETED_AT,
    TaskColumn.COMPLETED_BY,
] as const;

/** A List column a card has a field for. */
export type TaskCardFieldId = (typeof CARD_FIELD_COLUMNS)[number];

/** The card fields with their own place on the card; `TaskBoardCard` renders them. */
export type PrimaryCardFieldId = TaskColumn.DUE_DATE | TaskColumn.ENTITIES;

/** The card fields shown as chips; `TaskBoardOptionalFields` renders them. */
export type OptionalCardFieldId = Exclude<TaskCardFieldId, PrimaryCardFieldId>;

const CARD_FIELD_COLUMN_IDS: ReadonlySet<string> = new Set(CARD_FIELD_COLUMNS);

/** Whether a card has a field for the List column `id`. */
export const isTaskCardFieldId = (id: string): id is TaskCardFieldId =>
    CARD_FIELD_COLUMN_IDS.has(id);

/** The card-only field showing the chat chip: no List column, the view's `isCommentsShown`. */
export const TASK_CARD_COMMENTS_FIELD = "comments";

/**
 * The card fields whose place doesn't follow the order (entities on their
 * own row, the comments last in the chip row), in the order the gear lists
 * them.
 */
export const TASK_CARD_FIXED_FIELDS: ReadonlySet<string> = new Set([
    TaskColumn.ENTITIES,
    TASK_CARD_COMMENTS_FIELD,
]);

/** `columns` as List shows them: left pinned, then unpinned, then right pinned. */
const inListOrder = (columns: ViewColumns): ViewColumns => {
    const { leftPinnedColumns, centerColumns, rightPinnedColumns } =
        separateColumnsByPin(columns);
    return [...leftPinnedColumns, ...centerColumns, ...rightPinnedColumns];
};

/** The fields a card shows: the view's visible List columns it has a field for, in List's order. */
export const getTaskCardFields = (columns: ViewColumns): string[] =>
    inListOrder(columns)
        .map(({ id }) => id)
        .filter(isTaskCardFieldId);

/** The card fields, plus the comments when the view shows them. */
export const getTaskBoardCardFields = ({
    columns,
    isCommentsShown,
}: {
    columns: ViewColumns;
    isCommentsShown: boolean;
}): string[] => [
    ...getTaskCardFields(columns),
    ...(isCommentsShown ? [TASK_CARD_COMMENTS_FIELD] : []),
];

/**
 * The column definitions of the List columns a card has a field for: the
 * Board gear lists them as List's column settings would, so it offers,
 * names and orders them as there.
 */
export const getTaskCardFieldConfigs = (
    columnConfigs: TaskColumnConfigs,
): Record<string, TaskColumnConfigs[TaskColumn]> =>
    Object.fromEntries(
        Object.entries(columnConfigs).filter(([id]) => isTaskCardFieldId(id)),
    );
